import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Sparkles, Calendar } from 'lucide-react';
import { type DebriefStage } from '@momentum/types';
import { Button } from '@/shared/components/ui/Button';
import { Dialog, DialogContent } from '@/shared/components/ui/Dialog';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Label } from '@/shared/components/ui/Label';
import { DatePickerInput } from '@/shared/components/inputs/DatePicker.input';
import { parseLocalYmd, formatLocalYmd } from '@/shared/utils/date-utils';
import type { FactDraft } from '../../shared/context/DebriefFactsStateContext';
import { STAGE_META } from '../utils/stageMeta';

export type FactDialogMode = 'add' | 'edit';

interface FactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: FactDialogMode;
  stage: DebriefStage;
  showDateField: boolean;
  initialTitle?: string;
  initialDescription?: string;
  initialDate?: string;
  onSave: (draft: FactDraft) => void;
}

export function FactDialog({
  open,
  onOpenChange,
  mode,
  stage,
  showDateField,
  initialTitle = '',
  initialDescription = '',
  initialDate = '',
  onSave,
}: FactDialogProps) {
  const { t } = useTranslation('debriefV2');
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [date, setDate] = useState(initialDate);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
      setDescription(initialDescription);
      setDate(initialDate);
    }
  }, [open, initialTitle, initialDescription, initialDate]);

  const stageLabel = t(STAGE_META[stage].labelKey);
  const breadcrumbAction =
    mode === 'add'
      ? t('gather.factDialog.breadcrumbNew')
      : t('gather.factDialog.breadcrumbEdit');

  const isValid = title.trim().length > 0 && description.trim().length > 0;

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      title: title.trim(),
      description: description.trim(),
      date: showDateField && date ? date : undefined,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        showCloseButton
        dismissibleByOutsideClick={false}
        overlayClassName="bg-black/60"
        className="bg-background border-white/20 shadow-2xl"
      >
        <div className="flex items-center gap-2 pb-4 border-b-2 border-border">
          <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <Sparkles className="w-3.5 h-3.5" />
            {t('gather.factDialog.breadcrumbRoot')}
            <ChevronRight className="w-2.5 h-2.5 rtl:rotate-180" />
            <span className="text-foreground/70">{stageLabel}</span>
            <ChevronRight className="w-2.5 h-2.5 rtl:rotate-180" />
            <span className="text-foreground/70">{breadcrumbAction}</span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="fact-dialog-title"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('gather.factDialog.titleLabel')}
            </Label>
            <Input
              id="fact-dialog-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('gather.factDialog.titlePlaceholder')}
              autoFocus
              className="min-h-[40px] text-sm"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="fact-dialog-description"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('gather.factDialog.descriptionLabel')}
            </Label>
            <Textarea
              id="fact-dialog-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('gather.factDialog.descriptionPlaceholder')}
              rows={5}
              className="text-sm leading-relaxed resize-none"
            />
          </div>

          {showDateField && (
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" aria-hidden />
                {t('gather.factDialog.dateLabel')}
              </Label>
              <DatePickerInput
                value={parseLocalYmd(date)}
                onChange={(picked) =>
                  setDate(picked ? formatLocalYmd(picked) : '')
                }
                className="w-fit min-w-[220px]"
                inputClassName="h-10 min-h-0 bg-white border-gray-300 text-sm hover:bg-white"
              />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('gather.factDialog.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid}
            className="bg-[#7c6bc4] hover:bg-[#6a5aae] h-8 px-3 text-[13px]"
          >
            {mode === 'add'
              ? t('gather.factDialog.create')
              : t('gather.factDialog.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
